// ============================================================================
// CabinetFavoritesTab.tsx — вкладка «Избранное» кабинета (заметки себе).
//
// Механизм уже существует в платформе: «Избранное» — обычный групповой чат с
// единственным участником (текущий пользователь), который сервер создаёт
// идемпотентно по составу (lib/chatFavorites.ts). Поэтому вкладка НЕ вводит
// новую сущность и не дублирует мессенджер: резолвит (или создаёт) этот чат
// существующими RPC MessengerService и показывает его общим ChatThread —
// текст, файлы, ответы, реакции, пересылка, офлайн-очередь работают штатно.
//
// Новых proto, миграций и серверных прав не требуется.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Star } from 'lucide-react';
import { ChatThread } from '@/components/chat/ChatThread';
import { ensureFavoritesChat } from '@/lib/chatFavorites';
import { useAuthStore } from '@/store/authStore';
import { useSecurityAccess } from '@/lib/securityRoles';
import { friendlyError } from '@/lib/errors';
import { createLogger } from '@/lib/logger';
import { Button } from '@/components/ui/button';

const log = createLogger('CabinetFavoritesTab');

export function CabinetFavoritesTab() {
  const user = useAuthStore((s) => s.user);
  const myId = String(user?.id || '');
  const userName = String(user?.name || user?.login || '');
  const { isAdmin } = useSecurityAccess();

  const [conversationId, setConversationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!myId) {
      setError('Не удалось определить текущего пользователя. Войдите в базу заново.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Существующий RPC: найти чат «только я» либо создать его (сервер
      // идемпотентен по составу участников — второй такой чат не появится).
      const id = await ensureFavoritesChat(myId);
      setConversationId(id);
    } catch (e) {
      log.error('Не удалось открыть «Избранное»', { error: String(e) });
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [myId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !conversationId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <Star className="h-6 w-6 text-muted-foreground" />
        <div className="max-w-md text-sm text-destructive break-words">
          {error || 'Чат «Избранное» недоступен'}
        </div>
        <Button size="sm" variant="outline" onClick={() => void load()}>
          <RefreshCw className="mr-1 h-3 w-3" />
          Повторить
        </Button>
      </div>
    );
  }

  return (
    <ChatThread
      key={conversationId}
      conversationId={conversationId}
      title="Избранное"
      memberIds={[myId]}
      users={[{ id: myId, name: userName || 'Я' }]}
      myId={myId}
      isAdmin={isAdmin}
      favoritesConversationId={conversationId}
      isFavorites
      markReadOnOpen
    />
  );
}
